import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormGroup, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';
import { HTTP_X_CalculatePayMortgageFasterSavings_Response, MortgageApiService } from '@api-new/mortgageservice';
import { TermReductionBarChartComponent } from '@platform/smart-search/components/term-reduction-bar-chart/term-reduction-bar-chart.component';
import { AddSmartSearchPropertyMortgageSelectItem } from '@platform/smart-search/models/add-smart-search-property-mortgage-select-item.model';
import {
  AddSmartSearchDialogFormRemortgagePayMortgageFaster,
  defaultAddSmartSearchDialogValue,
} from '@platform/smart-search/utils/formUtils';
import { FormFieldWrapperComponent } from '@shared-lib/components/form-components/form-field-wrapper/form-field-wrapper.component';
import { InputNumberComponent, InputNumberType } from '@shared-lib/components/form-components/input-number/input-number.component';
import { IconComponent } from '@shared-lib/components/icon/icon.component';
import { TermPipe } from '@shared-lib/pipes/term.pipe';
import { StepperComponent } from '@shared/components/stepper/stepper.component';
import { CollapseDirective } from '@shared/directives/collapse.directive';
import { ButtonModule } from 'primeng/button';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'cp-add-smart-search-remortgage-pay-mortgage-faster',
  imports: [
    CommonModule,
    StepperComponent,
    FormFieldWrapperComponent,
    ReactiveFormsModule,
    InputNumberComponent,
    CollapseDirective,
    ButtonModule,
    IconComponent,
    TermPipe,
    TermReductionBarChartComponent,
  ],
  templateUrl: './add-smart-search-remortgage-pay-mortgage-faster.component.html',
  styleUrls: ['../add-smart-search-dialog-step.component.scss', './add-smart-search-remortgage-pay-mortgage-faster.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddSmartSearchRemortgagePayMortgageFasterComponent implements OnInit {
  @Input({ required: true }) formGroupName: string;
  @Input({ required: true }) currentPropertyMortgageData: AddSmartSearchPropertyMortgageSelectItem;
  @Input({ required: true }) payMortgageFasterSaving: HTTP_X_CalculatePayMortgageFasterSavings_Response;
  @Input({ required: true }) isLoadingPayMortgageFasterSaving: boolean;
  @Input() isBackButtonVisible = true;
  @Output() nextClicked = new EventEmitter();
  @Output() backClicked = new EventEmitter();
  @Output() calculatePayMortgageFasterSaving = new EventEmitter<{ termInMonths?: number; monthlyPaymentIncrease?: number }>();

  readonly mortgageApiService = inject(MortgageApiService);

  private readonly rootFormGroup = inject(FormGroupDirective);

  protected isShowMoreInsight = false;

  protected stepFormGroup: FormGroup<AddSmartSearchDialogFormRemortgagePayMortgageFaster>;
  protected readonly InputNumberType = InputNumberType;

  ngOnInit(): void {
    this.stepFormGroup = this.rootFormGroup.control.get(
      this.formGroupName,
    ) as FormGroup<AddSmartSearchDialogFormRemortgagePayMortgageFaster>;
    this.stepFormGroup.patchValue({
      termInMonths: this.currentPropertyMortgageData.mortgageTermRemaining,
    });

    if (
      this.stepFormGroup.controls.monthlyPaymentIncrease.value ===
      defaultAddSmartSearchDialogValue.remortgagePayMortgageFaster.monthlyPaymentIncrease
    ) {
      const monthlyPaymentIncrease = Math.round((this.currentPropertyMortgageData.mortgageCurrentMonthlyPayment * 0.1) / 10) * 10;
      this.stepFormGroup.patchValue({
        monthlyPaymentIncrease,
      });
    }
    this.calculatePayMortgageFasterSaving.emit({ monthlyPaymentIncrease: this.stepFormGroup.controls.monthlyPaymentIncrease.value });

    this.stepFormGroup.controls.monthlyPaymentIncrease.valueChanges.pipe(debounceTime(500)).subscribe((monthlyPaymentIncrease) => {
      if (this.stepFormGroup.controls.monthlyPaymentIncrease.valid) {
        this.calculatePayMortgageFasterSaving.emit({ monthlyPaymentIncrease });
      }
    });
  }

  emitNextClicked(): void {
    if (this.stepFormGroup.valid) {
      this.nextClicked.emit();
    }
  }

  emitBackClicked(): void {
    this.backClicked.emit();
  }
}
